import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import { CURRENCIES, symbolFor } from "../currency";
import { useAuth } from "../auth";
import AssignmentsModal from "../components/AssignmentsModal";
import BatchAssignModal from "../components/BatchAssignModal";
import ConfirmModal from "../components/ConfirmModal";
import ConfirmTypeModal from "../components/ConfirmTypeModal";
import ImportTasksModal, { formatImportLines } from "../components/ImportTasksModal";
import InboundChangesModal from "../components/InboundChangesModal";
import InvoiceModal from "../components/InvoiceModal";
import SyncLogModal from "../components/SyncLogModal";
import MyProposalsModal from "../components/MyProposalsModal";
import PaySplitPanel from "../components/PaySplitPanel";
import SheetPayoutsModal from "../components/SheetPayoutsModal";
import SnapshotsModal from "../components/SnapshotsModal";
import TaskDetailModal, { hrefFor } from "../components/TaskDetailModal";
import TaskProposalsModal from "../components/TaskProposalsModal";
import BusyOverlay from "../components/BusyOverlay";
import { useBusy } from "../useBusy";
import { downloadText, slug } from "../download";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// The inbound check answers `syncing` while the page still owes Google an
// outbound push (the tab is behind us then, so a diff would read our own
// un-pushed tasks back as client deletions). Wait for the push to land instead
// of showing anything. Backed off rather than fixed-interval: opening a project
// enqueues a push for every one of its pages, so this page's can be sitting
// behind a dozen others in the worker's queue. ~1 minute of patience total.
const INBOUND_SYNC_RETRIES = 6;
const inboundRetryDelay = (attempt) => Math.min(2000 * 2 ** attempt, 15000);

const fmtPrice = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return String(v ?? "");
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

const COLUMN_META = {
  // The client-facing task number: assigned by the server, counts up across
  // every page of the project, and is what the linked Google Sheet shows too.
  project_seq: { label: "#", className: "col-seq num", type: "seq" },
  name: { label: "Task", className: "col-name", type: "text" },
  client_estimate_hours: { label: "Client · hours", className: "col-num num", type: "number" },
  client_estimate_price: { label: "Client · price", className: "col-price num", type: "price" },
  // Free-form note the client can write, here or in the linked sheet's Note column.
  client_notes: { label: "Note", className: "col-notes", type: "textarea" },
  // The employee's own note on a task, for the admin to read. Never shown to
  // the client (the server doesn't even send it to them).
  employee_notes: { label: "Employee Note", className: "col-notes", type: "textarea" },
  assigned: { label: "Assigned", className: "col-assigned", type: "assigned" },
  my_pay: { label: "My pay", className: "col-num num", type: "my_pay" },
  agreed: { label: "Agreed", className: "col-agreed", type: "bool" },
};

export default function SheetDetail() {
  const { sheetId } = useParams();
  const { user } = useAuth();
  // Blocks the page (spinner + no pointer events) while a mutating request runs,
  // and drops re-entrant clicks so add/delete/etc. can't double-fire.
  const { busy, run } = useBusy();
  const [sheet, setSheet] = useState(null);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sortDir, setSortDir] = useState("desc"); // 'desc' | 'asc'
  const [paidModal, setPaidModal] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [deleteSelectedOpen, setDeleteSelectedOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [empImportOpen, setEmpImportOpen] = useState(false);
  // Tasks employees have proposed (employee → admin review), the same UI shape
  // as the Google Sheet inbound review below but its own separate flow.
  const [proposals, setProposals] = useState([]);
  const [proposalsOpen, setProposalsOpen] = useState(false);
  // The employee's own view of what they've proposed here recently — fetched
  // lazily when they open the popup, not on every page load.
  const [myProposals, setMyProposals] = useState([]);
  const [myProposalsOpen, setMyProposalsOpen] = useState(false);
  const [myProposalsBusy, setMyProposalsBusy] = useState(false);
  const [assignmentsTask, setAssignmentsTask] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [payoutsOpen, setPayoutsOpen] = useState(false);
  // Invoices for this milestone (super admin only; the server enforces it).
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [projectEmployees, setProjectEmployees] = useState([]);
  const [projectMemberships, setProjectMemberships] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [batchOpen, setBatchOpen] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Inbound (sheet → system) review: the active change set, the popup, history.
  const [inbound, setInbound] = useState(null);
  const [inboundOpen, setInboundOpen] = useState(false);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [syncLogOpen, setSyncLogOpen] = useState(false);
  const [checkMsg, setCheckMsg] = useState("");
  // Sheet-level pay split (admin only) — feeds the summary "Team cost" / "Your
  // share" cards. Same source the PaySplitPanel uses; comp math stays server-side.
  const [paySplit, setPaySplit] = useState(null);
  // True when the linked sheet's rows carry no recognisable IDs, so the diff is
  // degenerate (all-add/all-delete) and must be recovered by a re-sync, not a
  // review. See the "needs_resync" response from the inbound check.
  const [needsResync, setNeedsResync] = useState(false);
  // Per-employee work report (admin only): pick an employee on this page and get
  // a short summary of what they worked on, built server-side from task names.
  const [reportEmp, setReportEmp] = useState("");
  const [report, setReport] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);

  const role = user?.role;
  const canSeePrice = role === "admin" || role === "client";
  const locked = !!sheet?.paid;

  const canEdit = (field, task) => {
    if (locked) return false;
    if (role === "admin") {
      // The employee's note is theirs: the admin reads it, doesn't rewrite it.
      if (field === "employee_notes") return false;
      if (task?.agreed && field === "client_estimate_price") return false;
      // When price is synced to hours, it's derived — not directly editable.
      if (sheet?.price_sync_enabled && field === "client_estimate_price") return false;
      return true;
    }
    // Employees write their own note and nothing else (Emp · minutes stays
    // hidden from the UI, though the field still exists server-side).
    if (role === "employee") return field === "employee_notes";
    if (role === "client") return field === "client_notes";
    return false;
  };

  const columns = useMemo(() => {
    const keys = ["project_seq", "name"];
    if (role === "admin" || role === "client") {
      keys.push("client_estimate_hours", "client_estimate_price", "client_notes");
    } else if (role === "employee") {
      // Employees read the client's note (it's the instruction for the work)
      // but can't write it, and never see the client's money columns.
      keys.push("client_notes");
    }
    if (role === "admin" || role === "employee") keys.push("employee_notes");
    if (role === "admin") keys.push("assigned");
    if (role === "employee") keys.push("my_pay");
    keys.push("agreed");
    return keys.map((k) => {
      const meta = { key: k, ...COLUMN_META[k] };
      // The hours column holds minutes-per-task when the page is set that way.
      if (k === "client_estimate_hours" && sheet?.time_unit === "minutes") {
        meta.label = "Client · minutes";
      }
      // Two notes side by side need telling apart, except for the client, who
      // only ever sees their own. The employee's column is theirs, so it's
      // "Your Note" to them, not a label about them.
      if (k === "client_notes" && role !== "client") meta.label = "Client Note";
      if (k === "employee_notes") meta.label = role === "employee" ? "Your Note" : "Employee Note";
      return meta;
    });
  }, [role, sheet?.time_unit]);

  // Sort column: admin/client by client price, employees by their own pay.
  const sortKey = canSeePrice
    ? "client_estimate_price"
    : role === "employee"
    ? "my_pay"
    : null;

  const sortedTasks = useMemo(() => {
    if (!sortKey) return tasks;
    return [...tasks].sort((a, b) => {
      const pa = a[sortKey];
      const pb = b[sortKey];
      const aHas = pa !== null && pa !== undefined && pa !== "";
      const bHas = pb !== null && pb !== undefined && pb !== "";
      if (!aHas && !bHas) return 0;
      if (!aHas) return 1; // nulls last
      if (!bHas) return -1;
      const na = Number(pa);
      const nb = Number(pb);
      return sortDir === "desc" ? nb - na : na - nb;
    });
  }, [tasks, sortDir, sortKey]);

  const total = useMemo(() => {
    if (!canSeePrice) return null;
    return tasks.reduce((sum, t) => {
      const p = t.client_estimate_price;
      if (p === null || p === undefined || p === "") return sum;
      return sum + Number(p);
    }, 0);
  }, [tasks, canSeePrice]);

  const load = async () => {
    const [s, t] = await Promise.all([
      api.get(`/sheets/${sheetId}`),
      api.get(`/sheets/${sheetId}/tasks`),
    ]);
    setSheet(s.data);
    setTasks(t.data);
    const p = await api.get(`/projects/${s.data.project_id}`);
    setProject(p.data);
    setProjectEmployees(p.data.employees || []);
    setProjectMemberships(p.data.memberships || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [sheetId]);

  useEffect(() => {
    if (role !== "admin") return;
    api.get("/google/status").then((r) => setGoogleStatus(r.data)).catch(() => {});
  }, [role]);

  useEffect(() => {
    if (role !== "admin") return;
    api
      .get(`/sheets/${sheetId}/pay-split`)
      .then((r) => setPaySplit(r.data))
      .catch(() => setPaySplit(null));
  }, [role, sheetId, tasks.length]);

  // On open: surface any already-detected review instantly (no Google call), and
  // auto-pop it if it's a fresh 'pending' set (not one already rejected). Skipped
  // for paid (locked) pages — they're read-only, so there's nothing to apply.
  useEffect(() => {
    if (role !== "admin" || sheet?.paid) return;
    let cancelled = false;
    api
      .get(`/sheets/${sheetId}/inbound/pending`)
      .then(({ data }) => {
        if (cancelled || !data.change_set) return;
        setInbound(data.change_set);
        if (data.change_set.status === "pending") setInboundOpen(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [role, sheetId, sheet?.paid]);

  // Same idea, for tasks employees have proposed: fetch what's pending and pop
  // it open. No Google call involved, so this is the only fetch needed.
  useEffect(() => {
    if (role !== "admin" || sheet?.paid) return;
    let cancelled = false;
    api
      .get(`/sheets/${sheetId}/task-proposals/pending`)
      .then(({ data }) => {
        if (cancelled) return;
        setProposals(data);
        if (data.length > 0) setProposalsOpen(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [role, sheetId, sheet?.paid]);

  // Then re-read the live sheet (one Google call) to pick up edits made since.
  useEffect(() => {
    if (role !== "admin" || sheet?.paid) return;
    if (!sheet?.google_spreadsheet_id || !googleStatus?.connected) return;
    let cancelled = false;
    let timer = null;
    const check = async (attempt) => {
      let data;
      try {
        ({ data } = await api.post(`/sheets/${sheetId}/inbound/check`));
      } catch {
        return;
      }
      if (cancelled) return;
      if (data.syncing) {
        // Outbound push still queued: ask again once it's had time to land.
        if (attempt < INBOUND_SYNC_RETRIES) {
          timer = setTimeout(() => check(attempt + 1), inboundRetryDelay(attempt));
        }
        return;
      }
      if (data.needs_resync) {
        // Degenerate diff — close any auto-popped review and prompt a re-sync.
        setNeedsResync(true);
        setInbound(null);
        setInboundOpen(false);
        return;
      }
      setNeedsResync(false);
      setInbound(data.change_set);
      if (data.change_set?.status === "pending") setInboundOpen(true);
      // The live sheet agrees with us, so close a review that the cheap
      // /inbound/pending fetch already popped from a now-stale stored set.
      else if (!data.change_set) setInboundOpen(false);
    };
    check(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, [role, sheet?.google_spreadsheet_id, googleStatus?.connected, sheetId, sheet?.paid]);

  const handleGoogleSheet = async () => {
    // Open a blank tab now (within the click gesture, so it's not popup-blocked),
    // then ask the server for a live sheet URL. The /google/open endpoint creates
    // the sheet if missing, syncs it, and recreates it if it was deleted in Drive.
    const win = window.open("about:blank", "_blank");
    if (win) {
      win.document.write(
        sheet.google_spreadsheet_id ? "Opening your Google Sheet…" : "Creating your Google Sheet…"
      );
    }
    setSyncing(true);
    try {
      const { data } = await api.post(`/sheets/${sheetId}/google/open`);
      if (win) win.location = data.url;
      else window.open(data.url, "_blank", "noopener");
      load(); // refresh linked state / synced time / warning
    } catch (e) {
      if (win) win.close();
    } finally {
      setSyncing(false);
    }
  };

  // Manually re-read the sheet and surface any edits. Flash "up to date" when
  // there's nothing pending.
  const checkForChanges = () =>
    run(async () => {
      const { data } = await api.post(`/sheets/${sheetId}/inbound/check`);
      if (data.syncing) {
        setCheckMsg("Still syncing to Google, try again in a moment");
        setTimeout(() => setCheckMsg(""), 3000);
        return;
      }
      if (data.needs_resync) {
        setNeedsResync(true);
        setInbound(null);
        setInboundOpen(false);
        setCheckMsg("This sheet needs a re-sync");
        setTimeout(() => setCheckMsg(""), 3000);
        return;
      }
      setNeedsResync(false);
      setInbound(data.change_set);
      if (data.change_set) {
        setInboundOpen(true);
      } else if (data.connected === false) {
        setCheckMsg("Google not connected");
        setTimeout(() => setCheckMsg(""), 3000);
      } else {
        setCheckMsg("Sheet is up to date");
        setTimeout(() => setCheckMsg(""), 3000);
      }
    });

  // Push our current tasks back into the linked sheet (re-writes the hidden IDs
  // + checkboxes) and clear the bogus review. No tasks are changed here.
  const resyncSheet = () =>
    run(async () => {
      await api.post(`/sheets/${sheetId}/inbound/resync`);
      setNeedsResync(false);
      setInbound(null);
      setInboundOpen(false);
      setCheckMsg("Sheet re-synced");
      setTimeout(() => setCheckMsg(""), 3000);
      await load();
    });

  // Fetch the work-report for the selected employee. Cleared when the picker is
  // reset to the "Employee…" placeholder.
  const loadReport = async (userId) => {
    setReportEmp(userId);
    setReport(null);
    if (!userId) return;
    setReportBusy(true);
    try {
      const { data } = await api.get(
        `/sheets/${sheetId}/employee-report`,
        { params: { user_id: userId } }
      );
      setReport(data);
    } catch (e) {
      // No fallback text pretending to be a real report: surface the actual
      // failure (Claude timed out, errored, or isn't configured) plainly.
      setReport({
        report: e?.response?.data?.detail || "Failed to generate a report for this employee.",
        error: true,
      });
    } finally {
      setReportBusy(false);
    }
  };

  const approveInbound = () =>
    run(async () => {
      await api.post(`/sheets/${sheetId}/inbound/${inbound.id}/approve`);
      setInboundOpen(false);
      setInbound(null);
      await load();
    });

  const rejectInbound = () =>
    run(async () => {
      await api.post(`/sheets/${sheetId}/inbound/${inbound.id}/reject`);
      setInboundOpen(false);
      // Keep it around (now 'rejected') so "Pending changes" can reopen it later.
      setInbound((cs) => (cs ? { ...cs, status: "rejected" } : cs));
    });

  const restoreSnapshot = (snapId) =>
    run(async () => {
      await api.post(`/sheets/${sheetId}/snapshots/${snapId}/restore`);
      setSnapshotsOpen(false);
      await load();
    });

  const viewProof = async (proofId) => {
    const res = await api.get(
      `/sheets/${sheetId}/payment-proofs/${proofId}/file`,
      { responseType: "blob" }
    );
    window.open(URL.createObjectURL(res.data), "_blank");
  };

  const approveProposal = (id) =>
    run(async () => {
      await api.post(`/task-proposals/${id}/approve`);
      setProposals((ps) => {
        const next = ps.filter((p) => p.id !== id);
        if (next.length === 0) setProposalsOpen(false);
        return next;
      });
      await load();
    });

  const rejectProposal = (id) =>
    run(async () => {
      await api.post(`/task-proposals/${id}/reject`);
      setProposals((ps) => {
        const next = ps.filter((p) => p.id !== id);
        if (next.length === 0) setProposalsOpen(false);
        return next;
      });
    });

  // Resolve every pending request in one go, through the same per-request
  // endpoints. Sequential, not parallel: approving assigns page_seq = max+1 per
  // task it creates, so two approvals in flight at once would collide on the
  // (sheet, page_seq) unique constraint — the same reason handleBulkImport
  // POSTs one row at a time. Whatever was resolved before a failure stays
  // resolved, so the list drops exactly those and keeps the rest reviewable.
  const resolveAllProposals = (action) =>
    run(async () => {
      const done = [];
      try {
        for (const p of proposals) {
          await api.post(`/task-proposals/${p.id}/${action}`);
          done.push(p.id);
        }
      } finally {
        setProposals((ps) => {
          const next = ps.filter((p) => !done.includes(p.id));
          if (next.length === 0) setProposalsOpen(false);
          return next;
        });
        if (action === "approve" && done.length > 0) await load();
      }
    });

  const updateField = async (taskId, key, value) => {
    const payload = { [key]: value };
    const { data } = await api.patch(`/tasks/${taskId}`, payload);
    setTasks((ts) => ts.map((t) => (t.id === taskId ? { ...t, ...data } : t)));
  };

  const updateSheetCurrency = (currency) =>
    run(async () => {
      const { data } = await api.patch(`/sheets/${sheetId}`, { currency });
      setSheet(data);
    });

  // Toggle/adjust price sync. The server re-derives every task's price from its
  // hours, so refresh the task list to reflect the new prices.
  const updateSheetSync = (patch) =>
    run(async () => {
      const { data } = await api.patch(`/sheets/${sheetId}`, patch);
      setSheet(data);
      const t = await api.get(`/sheets/${sheetId}/tasks`);
      setTasks(t.data);
    });

  const confirmDeleteTask = () =>
    run(async () => {
      await api.delete(`/tasks/${deleteTaskId}`);
      setDeleteTaskId(null);
      await load();
    });

  // One endpoint rather than a DELETE per task: the whole selection is deleted
  // in a single transaction, and the page is queued for one Google push instead
  // of one per task. Nothing is written if any selected task fails validation,
  // so a rejected batch leaves the confirm open and the page untouched.
  const confirmDeleteSelected = () =>
    run(async () => {
      await api.post("/tasks/batch-delete", { task_ids: Array.from(selectedIds) });
      setDeleteSelectedOpen(false);
      clearSelection();
      await load();
    });

  const togglePaid = (file) =>
    run(async () => {
      if (locked) {
        // Unmark — no file needed.
        await api.post(`/sheets/${sheetId}/unmark-paid`);
      } else {
        // Mark paid — file required (modal enforces).
        const form = new FormData();
        form.append("file", file);
        await api.post(`/sheets/${sheetId}/mark-paid`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setPaidModal(false);
      await load();
    });

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === tasks.length) return new Set();
      return new Set(tasks.map((t) => t.id));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Writes the selected tasks back out as a .txt in the import format, the same
  // file the proposal review popup downloads and the same serializer, so a
  // selection can be moved onto another page (or another project) by pasting it
  // into "Import from text…" there. Client-side only, no endpoint.
  const exportSelected = () => {
    const timeUnit = sheet?.time_unit === "minutes" ? "minutes" : "hours";
    // Export in the order shown, so the file matches whatever column the admin
    // has the table sorted by.
    const picked = sortedTasks.filter((t) => selectedIds.has(t.id));
    const text = formatImportLines(
      picked.map((t) => ({
        ...t,
        // The format carries exactly one assignee and the importer matches it by
        // exact name, so a task shared by two people can't be expressed: it's
        // written with no assignee rather than silently losing one of them.
        assignee: (t.assignments || []).length === 1 ? t.assignments[0].name : null,
      })),
      // The page's rate turns a money-only estimate into hours; see
      // formatEstimate. Null (no rate set) just means such a task exports as
      // money, the way it always did.
      { timeUnit, hourlyRate: sheet?.hourly_rate }
    );
    downloadText(`${slug(sheet?.name) || "tasks"}.txt`, text);
  };

  const handleBulkImport = (parsed, opts = {}) =>
    run(async () => {
      const agreed = !!opts.agreed;
      // The time column holds hours or minutes per the sheet's time_unit; convert
      // the parsed minutes accordingly. Under price sync the backend derives price.
      const timeUnit = sheet?.time_unit === "minutes" ? "minutes" : "hours";
      // Sequential, not parallel: each create assigns page_seq = max+1, so parallel
      // POSTs would collide on the (sheet, page_seq) unique constraint. A matched
      // assignee gets a follow-up assignment call once the task id is known.
      for (const t of parsed) {
        const payload = { name: t.name, agreed };
        if (t.url) payload.url = t.url;
        if (t.minutes !== undefined && t.minutes !== null) {
          // Convert the parsed minutes into whatever unit this sheet's time column
          // holds. Round hours to the column's 2 decimals so the stored hours and
          // the price-synced price stay self-consistent (hours × rate = price).
          payload.client_estimate_hours =
            timeUnit === "minutes" ? t.minutes : Number((t.minutes / 60).toFixed(2));
        } else if (t.price !== undefined && t.price !== null) {
          payload.client_estimate_price = t.price;
        }
        const res = await api.post(`/sheets/${sheetId}/tasks`, payload);
        if (t.assigneeUserId) {
          await api.put(`/tasks/${res.data.id}/assignments`, [
            { user_id: t.assigneeUserId, fixed_amount: null },
          ]);
        }
      }
      await load();
    });

  // Employees can't create tasks directly: this submits the whole parsed batch
  // as one proposal for the admin to review. Same field mapping as the admin's
  // own import; any parsed assignee is simply never read here — approving
  // always assigns the resulting tasks to whoever submitted them.
  const handleEmployeeImport = (parsed) =>
    run(async () => {
      const timeUnit = sheet?.time_unit === "minutes" ? "minutes" : "hours";
      const items = parsed.map((t) => {
        const item = { name: t.name };
        if (t.url) item.url = t.url;
        if (t.minutes !== undefined && t.minutes !== null) {
          item.client_estimate_hours =
            timeUnit === "minutes" ? t.minutes : Number((t.minutes / 60).toFixed(2));
        } else if (t.price !== undefined && t.price !== null) {
          item.client_estimate_price = t.price;
        }
        return item;
      });
      await api.post(`/sheets/${sheetId}/task-proposals`, { items });
      setCheckMsg(`Submitted ${items.length} task${items.length === 1 ? "" : "s"} for review`);
      setTimeout(() => setCheckMsg(""), 3000);
    });

  // Lazy-loaded on open (not on every page visit) — the employee's own recent
  // requests and where each one stands. Uses its own busy flag rather than the
  // page-wide one, so opening this popup doesn't block the rest of the page.
  const openMyProposals = async () => {
    setMyProposalsOpen(true);
    setMyProposalsBusy(true);
    try {
      const { data } = await api.get(`/sheets/${sheetId}/task-proposals/mine`);
      setMyProposals(data);
    } catch {
      setMyProposals([]);
    } finally {
      setMyProposalsBusy(false);
    }
  };

  const toggleSort = () => {
    setSortDir((d) => (d === "desc" ? "asc" : "desc"));
  };

  const exportXlsx = async () => {
    const res = await api.get(`/sheets/${sheetId}/export.xlsx`, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `${sheet?.name || "tasks"}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  };

  if (!sheet) return <div className="loading">Loading</div>;

  const sheetSymbol = symbolFor(sheet.currency);

  // Summary cards shown above the table (summary-before-detail). Values come
  // straight from data already loaded (tasks + pay-split); no comp math here.
  const agreedCount = tasks.filter((t) => t.agreed).length;
  const agreedSum = canSeePrice
    ? tasks
        .filter((t) => t.agreed)
        .reduce((s, t) => s + (Number(t.client_estimate_price) || 0), 0)
    : 0;
  const plural = (n) => (n === 1 ? "" : "s");

  const statCards = [];
  if (canSeePrice) {
    statCards.push({
      k: "Client total",
      v: `${sheetSymbol}${fmtPrice(total || 0)}`,
      sub: sheet.price_sync_enabled
        ? `${tasks.length} task${plural(tasks.length)} · price-synced`
        : `${tasks.length} task${plural(tasks.length)}`,
    });
  } else if (role === "employee") {
    statCards.push({
      k: "My total",
      v: sheet.my_pay != null ? `${sheetSymbol}${fmtPrice(sheet.my_pay)}` : "—",
      sub: `${tasks.length} task${plural(tasks.length)}`,
    });
  }
  if (role === "admin") {
    const teamCost = paySplit
      ? Math.max(0, Number(paySplit.total || 0) - Number(paySplit.admin_share || 0))
      : null;
    const assignees = paySplit
      ? (paySplit.breakdown || []).filter((r) => Number(r.amount) > 0).length
      : 0;
    const marginPct =
      paySplit && Number(paySplit.total) > 0
        ? Math.round((Number(paySplit.admin_share) / Number(paySplit.total)) * 100)
        : null;
    statCards.push({
      k: "Team cost",
      v: teamCost != null ? `${sheetSymbol}${fmtPrice(teamCost)}` : "—",
      sub: teamCost != null ? `${assignees} assignee${plural(assignees)}` : "loading…",
    });
    statCards.push({
      k: "Your share",
      v: paySplit ? `${sheetSymbol}${fmtPrice(paySplit.admin_share || 0)}` : "—",
      sub: marginPct != null ? `${marginPct}% margin` : "",
    });
  }
  statCards.push({
    k: "Agreed",
    v: `${agreedCount}`,
    vsub: `/ ${tasks.length}`,
    sub: canSeePrice
      ? agreedSum > 0
        ? `${sheetSymbol}${fmtPrice(agreedSum)} locked`
        : "none locked"
      : `${Math.max(0, tasks.length - agreedCount)} open`,
  });
  if (statCards.length < 3) {
    statCards.push({ k: "Tasks", v: `${tasks.length}`, sub: "on this page" });
  }

  return (
    <>
      <div className="page-head">
        <div className="title-block">
          <div className="crumbs">
            <Link to="/">Projects</Link>
            <span className="sep">/</span>
            <Link to={`/projects/${sheet.project_id}`}>{project?.name || "Project"}</Link>
          </div>
          <div className="title-main">
            <div className="row-h" style={{ gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              <h1 style={{ margin: 0 }}>{sheet.name}</h1>
              <span className={`paid-pill${locked ? "" : " not-paid"}`}>
                {locked ? "Paid" : "Not paid"}
              </span>
              {role === "admin" && sheet.employee_unpaid_amount != null && (
                <span
                  className="owe-pill"
                  title="The client has paid for this milestone, but an employee who earned money on it hasn't been marked paid yet"
                >
                  Owe employee {sheetSymbol}{fmtPrice(sheet.employee_unpaid_amount)}
                </span>
              )}
            </div>
            <div className="meta-line" style={{ gap: 8 }}>
              <span className="label">Tasks</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 14 }}>{tasks.length}</span>
            </div>
          </div>
        </div>

        <div className="meta-line" style={{ flexWrap: "wrap", justifyContent: "flex-end", gap: 8, alignItems: "center" }}>
          {role === "admin" && !locked && (
            <label className="head-select" title="Whether the time column holds hours or minutes per task">
              <span>Time in</span>
              <select
                className="currency-pill"
                value={sheet.time_unit}
                onChange={(e) => updateSheetSync({ time_unit: e.target.value })}
              >
                <option value="hours">hours</option>
                <option value="minutes">minutes</option>
              </select>
            </label>
          )}
          {role === "admin" && !locked && (
            <select
              className="currency-pill"
              value={sheet.currency}
              onChange={(e) => updateSheetCurrency(e.target.value)}
              title="Change currency for this milestone"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.symbol} {c.code}
                </option>
              ))}
            </select>
          )}
          {role === "admin" && !locked && (
            <button onClick={() => setPayoutsOpen(true)}>Milestone payouts…</button>
          )}
          {locked && sheet.incoming_proof_id && (role === "admin" || role === "client") && (
            <button className="ghost" onClick={() => viewProof(sheet.incoming_proof_id)}>
              View proof
            </button>
          )}
          {role === "admin" && user?.is_superadmin && (
            <button
              className="ghost"
              onClick={() => setInvoiceOpen(true)}
              title="Generate an invoice for this milestone, or download an earlier one"
            >
              Invoice…
            </button>
          )}
          {(role === "admin" || role === "client") && tasks.length > 0 && (
            <button
              className="icon-btn export-btn"
              onClick={exportXlsx}
              title="Export the client-facing task list to Excel (.xlsx)"
            >
              <ExportXlsxIcon size={24} />
            </button>
          )}
          {role === "admin" && googleStatus?.connected && (
            <button
              className="icon-btn sheet-btn"
              onClick={handleGoogleSheet}
              disabled={syncing}
              title={
                sheet.google_spreadsheet_id
                  ? "Sync & open this page's tab in the project's Google Sheet"
                  : "Create this page's tab in the project's Google Sheet"
              }
            >
              {syncing ? <Spinner size={22} /> : <GoogleSheetsIcon size={26} />}
            </button>
          )}
          {role === "admin" && !locked && googleStatus?.connected && sheet.google_spreadsheet_id && (
            <button
              className="ghost"
              onClick={checkForChanges}
              title="Re-read the linked Google Sheet and review any edits made there"
            >
              Check sheet
            </button>
          )}
          {checkMsg && <span className="check-msg">{checkMsg}</span>}
          {role === "admin" && !locked && inbound && (
            <button
              className="primary pending-changes-btn"
              onClick={() => setInboundOpen(true)}
              title="Review changes made in the Google Sheet"
            >
              Pending changes
              <span className="pending-badge">
                {(inbound.summary?.updates || 0) +
                  (inbound.summary?.adds || 0) +
                  (inbound.summary?.deletes || 0)}
              </span>
            </button>
          )}
          {role === "admin" && !locked && proposals.length > 0 && (
            <button
              className="primary pending-changes-btn"
              onClick={() => setProposalsOpen(true)}
              title="Review tasks proposed by employees"
            >
              Task requests
              <span className="pending-badge">
                {proposals.reduce((s, p) => s + p.items.length, 0)}
              </span>
            </button>
          )}
          {role === "admin" && googleStatus?.connected && (
            <button className="ghost" onClick={() => setSnapshotsOpen(true)} title="Snapshots & history">
              History
            </button>
          )}
          {role === "admin" && googleStatus?.connected && (
            <button
              className="ghost"
              onClick={() => setSyncLogOpen(true)}
              title="Every sync between this milestone and its Google tab"
            >
              Sync log
            </button>
          )}
          {role === "admin" && (
            <button onClick={() => setPaidModal(true)}>
              {locked ? "Mark as unpaid" : "Mark as paid"}
            </button>
          )}
        </div>
      </div>

      {tasks.length > 0 && (
        <div className="stat-strip">
          {statCards.map((c) => (
            <div className="stat-card" key={c.k}>
              <div className="stat-k">{c.k}</div>
              <div className="stat-v">
                {c.v}
                {c.vsub && <span className="stat-vsub">{c.vsub}</span>}
              </div>
              {c.sub && <div className="stat-sub">{c.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {role === "admin" && (
        <PaySplitPanel
          sheetId={sheetId}
          reloadKey={tasks.length}
          title="Pay split for this milestone"
          onChange={load}
        />
      )}

      {role === "admin" && (
        <div className="card pad work-report">
          <div className="section-head">
            <h3 style={{ margin: 0 }}>Work report</h3>
            <span className="muted-sm">
              A short summary of what an employee worked on, from their task names.
            </span>
          </div>
          <div className="work-report-row">
            <select
              className="currency-pill"
              value={reportEmp}
              onChange={(e) => loadReport(e.target.value)}
            >
              <option value="">Select employee…</option>
              {(paySplit?.breakdown || [])
                .filter((r) => Number(r.assigned_count) > 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((r) => (
                  <option key={r.user_id} value={r.user_id}>
                    {r.name}
                  </option>
                ))}
            </select>
            {reportBusy && <Spinner size={18} />}
          </div>
          {report && !reportBusy && (
            report.error ? (
              <p className="error" style={{ marginTop: 8 }}>{report.report}</p>
            ) : (
              <p className="work-report-text">
                {report.ai && <span className="ai-chip">AI</span>}
                {report.report}
              </p>
            )
          )}
          {!reportEmp && !reportBusy && (
            <p className="muted-sm" style={{ marginTop: 8 }}>
              {(paySplit?.breakdown || []).some((r) => Number(r.assigned_count) > 0)
                ? "Pick an employee to generate their report."
                : "No employees are assigned to tasks on this page yet."}
            </p>
          )}
        </div>
      )}

      {role === "admin" && sheet.sync_warning && (
        <div className="banner warn" style={{ marginBottom: 16, whiteSpace: "pre-wrap" }}>
          <strong>Google Sheet:</strong> {sheet.sync_warning}
        </div>
      )}

      {role === "admin" && !locked && needsResync && (
        <div className="resync-banner">
          <div className="resync-text">
            <strong>This sheet needs a re-sync to enable change tracking.</strong>
            <span>
              Its rows don't carry the hidden IDs we use to match edits back to your
              tasks — it was created before change tracking, or the ID column was
              cleared. Re-sync pushes your current tasks back into the sheet (writing
              the IDs and checkboxes). Your tasks here are not changed.
            </span>
          </div>
          <button className="primary" onClick={resyncSheet} style={{ flexShrink: 0 }}>
            Re-sync sheet
          </button>
        </div>
      )}

      {role === "employee" && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
          <button className="ghost" onClick={openMyProposals}>My requests…</button>
          {!locked && (
            <button onClick={() => setEmpImportOpen(true)}>Import from text…</button>
          )}
        </div>
      )}

      {role === "admin" && !locked && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button onClick={() => setImportOpen(true)}>Import from text…</button>
          </div>
          <div className="price-sync-bar">
            <label className="psync-toggle" title="Keep every price equal to hours × your rate">
              <input
                type="checkbox"
                className="cbx"
                checked={!!sheet.price_sync_enabled}
                onChange={(e) => updateSheetSync({ price_sync_enabled: e.target.checked })}
              />
              <span>Sync price to hours</span>
            </label>
            <div className="psync-rate-group">
              {/* Editable whether or not price sync is on. With sync on it drives
                  every price; with sync off nothing derives from it, but the text
                  export still divides a money-only estimate by it to write hours
                  (see formatEstimate), and with no rate such a task can only
                  export as money. */}
              <div
                className="price-cell psync-rate"
                title="Used to derive prices when price sync is on, and to write money estimates as hours in the text export"
              >
                <span className="prefix">{sheetSymbol}</span>
                <MoneyInput
                  amount={sheet.hourly_rate}
                  placeholder="rate"
                  onCommit={(v) => updateSheetSync({ hourly_rate: v })}
                />
              </div>
              <span className="psync-suffix">per hour</span>
            </div>
          </div>
          <div className="price-sync-bar">
            <label
              className="psync-toggle"
              title="Every task on this page priced at or under this is ticked Agreed automatically, however it got here. Untick one by hand and it stays unticked."
            >
              <input
                type="checkbox"
                className="cbx"
                checked={!!sheet.auto_approve_enabled}
                onChange={(e) => updateSheetSync({ auto_approve_enabled: e.target.checked })}
              />
              <span>Auto-approve tasks up to</span>
            </label>
            <div className="psync-rate-group">
              <div className={`price-cell psync-rate${sheet.auto_approve_enabled ? "" : " is-disabled"}`}>
                <span className="prefix">{sheetSymbol}</span>
                <MoneyInput
                  amount={sheet.auto_approve_max_price}
                  disabled={!sheet.auto_approve_enabled}
                  placeholder="limit"
                  onCommit={(v) => updateSheetSync({ auto_approve_max_price: v })}
                />
              </div>
              <span className="psync-suffix">mark as agreed</span>
            </div>
          </div>
        </>
      )}

      {role === "admin" && !locked && selectedIds.size > 0 && (
        <div className="batch-toolbar">
          <span className="batch-count">
            {selectedIds.size} task{selectedIds.size === 1 ? "" : "s"} selected
          </span>
          <div className="row-h" style={{ gap: 8 }}>
            <button onClick={() => setBatchOpen(true)} className="primary">Batch assign…</button>
            <button
              onClick={exportSelected}
              title="Download the selected tasks as a text list you can paste into Import from text… on another page"
            >
              Export
            </button>
            <button
              className="danger-action"
              onClick={() => setDeleteSelectedOpen(true)}
              title="Delete every selected task"
            >
              Delete
            </button>
            <button onClick={clearSelection}>Clear</button>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="tasks">
          <thead>
            <tr>
              {role === "admin" && !locked && (
                <th className="col-select">
                  <div className="select-cell">
                    <input
                      type="checkbox"
                      className="cbx"
                      checked={tasks.length > 0 && selectedIds.size === tasks.length}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            selectedIds.size > 0 && selectedIds.size < tasks.length;
                        }
                      }}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </div>
                </th>
              )}
              {columns.map((c) => {
                const sortable = c.key === sortKey;
                return (
                  <th
                    key={c.key}
                    className={c.className}
                    onClick={sortable ? toggleSort : undefined}
                    style={sortable ? { cursor: "pointer", userSelect: "none" } : undefined}
                    title={sortable ? `Sort ${sortDir === "desc" ? "ascending" : "descending"}` : undefined}
                  >
                    {c.label}
                    {sortable && (
                      <span style={{ marginLeft: 4, color: "var(--accent)" }}>
                        {sortDir === "desc" ? "↓" : "↑"}
                      </span>
                    )}
                  </th>
                );
              })}
              {role === "admin" && <th className="col-actions"></th>}
            </tr>
          </thead>
          <tbody>
            {sortedTasks.length === 0 && (
              <tr>
                <td colSpan={columns.length + (role === "admin" ? (locked ? 1 : 2) : 0)}>
                  <div className="empty" style={{ padding: 40 }}>
                    No tasks yet.
                    {role === "admin" && <small>Add the first one above.</small>}
                  </div>
                </td>
              </tr>
            )}
            {sortedTasks.map((t) => (
              <tr key={t.id} className={selectedIds.has(t.id) ? "row-selected" : undefined}>
                {role === "admin" && !locked && (
                  <td className="col-select">
                    <div className="select-cell">
                      <input
                        type="checkbox"
                        className="cbx"
                        checked={selectedIds.has(t.id)}
                        onChange={() => toggleSelect(t.id)}
                      />
                    </div>
                  </td>
                )}
                {columns.map((c) => (
                  <td key={c.key} className={c.className}>
                    <TaskCell
                      task={t}
                      col={c}
                      canEdit={canEdit(c.key, t)}
                      onSave={updateField}
                      currencySymbol={sheetSymbol}
                      onOpenAssignments={() => setAssignmentsTask(t)}
                      onOpenDetail={() => setDetailTask(t)}
                    />
                  </td>
                ))}
                {role === "admin" && (
                  <td className="col-actions">
                    {!locked && (
                      <button className="ghost icon trash-btn" title="Delete task" onClick={() => setDeleteTaskId(t.id)}>
                        <TrashIcon />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {(canSeePrice && sortedTasks.length > 0) ||
          (role === "employee" && sheet.my_pay != null) ? (
            <tfoot>
              <tr className="totals-row">
                {role === "admin" && !locked && <td className="col-select"></td>}
                {columns.map((c) => (
                  <td key={c.key} className={c.className}>
                    {c.key === "name" && (
                      <strong>{role === "employee" ? "My total" : "Total"}</strong>
                    )}
                    {c.key === "client_estimate_price" && canSeePrice && (
                      <span className="num" style={{ padding: "9px 12px", display: "block", fontWeight: 600 }}>
                        {sheetSymbol}{fmtPrice(total)}
                      </span>
                    )}
                    {c.key === "my_pay" && role === "employee" && sheet.my_pay != null && (
                      <span className="num" style={{ padding: "9px 12px", display: "block", fontWeight: 600 }}>
                        {sheetSymbol}{fmtPrice(sheet.my_pay)}
                      </span>
                    )}
                  </td>
                ))}
                {role === "admin" && <td className="col-actions"></td>}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <ConfirmTypeModal
        open={paidModal}
        title={locked ? "Mark this milestone as unpaid?" : "Mark this milestone as paid?"}
        description={
          locked
            ? "Unmarking will re-enable editing on this milestone. The payment proof will be removed."
            : "Once marked as paid, this milestone and all of its tasks become read-only. Upload the supporting screenshot or PDF of the payment."
        }
        phrase={locked ? "Client has not paid" : "Client has paid"}
        confirmLabel={locked ? "Mark as unpaid" : "Mark as paid"}
        requireFile={!locked}
        fileLabel="Proof of payment (image or PDF)"
        onConfirm={togglePaid}
        onCancel={() => setPaidModal(false)}
      />

      <BusyOverlay show={busy} label="Working…" />

      <ConfirmModal
        open={deleteTaskId !== null}
        title="Delete this task?"
        description="This task will be permanently deleted. This cannot be undone."
        confirmLabel="Delete task"
        danger
        onConfirm={confirmDeleteTask}
        onCancel={() => setDeleteTaskId(null)}
      />

      <ConfirmModal
        open={deleteSelectedOpen}
        title={`Delete ${selectedIds.size} task${selectedIds.size === 1 ? "" : "s"}?`}
        description="The selected tasks will be permanently deleted. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} task${selectedIds.size === 1 ? "" : "s"}`}
        danger
        onConfirm={confirmDeleteSelected}
        onCancel={() => setDeleteSelectedOpen(false)}
      />

      <ImportTasksModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleBulkImport}
        currency={sheet.currency}
        employees={projectEmployees}
        priceSyncEnabled={!!sheet.price_sync_enabled}
      />

      <ImportTasksModal
        open={empImportOpen}
        onClose={() => setEmpImportOpen(false)}
        onImport={handleEmployeeImport}
        currency={sheet.currency}
        priceSyncEnabled={!!sheet.price_sync_enabled}
        employeeMode
      />

      <InboundChangesModal
        open={inboundOpen}
        changeSet={inbound}
        currencySymbol={sheetSymbol}
        busy={busy}
        onApprove={approveInbound}
        onReject={rejectInbound}
        onClose={() => setInboundOpen(false)}
      />

      <SyncLogModal
        open={syncLogOpen}
        sheetId={sheetId}
        onClose={() => setSyncLogOpen(false)}
      />

      <InvoiceModal
        open={invoiceOpen}
        sheetId={sheetId}
        onClose={() => setInvoiceOpen(false)}
      />

      <TaskProposalsModal
        open={proposalsOpen}
        proposals={proposals}
        currencySymbol={sheetSymbol}
        timeUnit={sheet.time_unit}
        hourlyRate={sheet.hourly_rate}
        sheetName={sheet.name}
        busy={busy}
        onApprove={approveProposal}
        onReject={rejectProposal}
        onApproveAll={() => resolveAllProposals("approve")}
        onRejectAll={() => resolveAllProposals("reject")}
        onClose={() => setProposalsOpen(false)}
      />

      <MyProposalsModal
        open={myProposalsOpen}
        proposals={myProposals}
        currencySymbol={sheetSymbol}
        timeUnit={sheet.time_unit}
        busy={myProposalsBusy}
        onClose={() => setMyProposalsOpen(false)}
      />

      <SnapshotsModal
        open={snapshotsOpen}
        sheetId={sheetId}
        currencySymbol={sheetSymbol}
        onClose={() => setSnapshotsOpen(false)}
        onRestore={restoreSnapshot}
      />

      <TaskDetailModal
        open={detailTask !== null}
        task={detailTask}
        canEdit={role === "admin" && !locked}
        onClose={() => setDetailTask(null)}
        onSave={updateField}
      />

      <AssignmentsModal
        open={assignmentsTask !== null}
        task={assignmentsTask}
        currency={sheet.currency}
        allEmployees={projectEmployees}
        memberships={projectMemberships}
        onClose={() => setAssignmentsTask(null)}
        onSaved={() => load()}
      />

      <SheetPayoutsModal
        open={payoutsOpen}
        sheet={sheet}
        allEmployees={projectEmployees}
        onClose={() => setPayoutsOpen(false)}
        onSaved={() => load()}
      />

      <BatchAssignModal
        open={batchOpen}
        taskIds={Array.from(selectedIds)}
        currency={sheet.currency}
        allEmployees={projectEmployees}
        memberships={projectMemberships}
        onClose={() => setBatchOpen(false)}
        onSaved={() => {
          clearSelection();
          load();
        }}
      />
    </>
  );
}

/* Money field that only reports up on blur/Enter, and only when it changed.
   Backs both the price-sync hourly rate and the auto-approve limit. */
function MoneyInput({ amount, onCommit, disabled = false, placeholder = "" }) {
  const [value, setValue] = useState(amount === null || amount === undefined ? "" : fmtPrice(amount));

  useEffect(() => {
    setValue(amount === null || amount === undefined ? "" : fmtPrice(amount));
  }, [amount]);

  const commit = () => {
    const v = num(value);
    if (v === num(amount)) return;
    onCommit(v);
  };

  return (
    <input
      className="cell-input num with-currency"
      type="number"
      step="5"
      min="0"
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
    />
  );
}

function GoogleSheetsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#0F9D58"
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
      />
      <path fill="#0A7E40" d="M14 2l6 6h-6z" />
      <path fill="#F1F1F1" d="M8 11.5h8v7.5H8z" />
      <g stroke="#0F9D58" strokeWidth="0.9">
        <line x1="8" y1="14" x2="16" y2="14" />
        <line x1="8" y1="16.5" x2="16" y2="16.5" />
        <line x1="12" y1="11.5" x2="12" y2="19" />
      </g>
    </svg>
  );
}

function Spinner({ size = 16 }) {
  return <span className="btn-spinner" style={{ width: size, height: size }} aria-label="Working" />;
}

function LinkIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function TrashIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function ExportXlsxIcon({ size = 24 }) {
  // Excel-green tile with a download arrow into a tray.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="3.5" fill="#1D6F42" />
      <g
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        <path d="M12 6.5v6.5" />
        <path d="M8.75 10.25 12 13.5l3.25-3.25" />
        <path d="M7.5 17h9" />
      </g>
    </svg>
  );
}

function TaskCell({ task, col, canEdit, onSave, currencySymbol, onOpenAssignments, onOpenDetail }) {
  const [value, setValue] = useState(task[col.key] ?? "");

  useEffect(() => {
    const raw = task[col.key];
    if (col.type === "bool") {
      setValue(!!raw);
    } else if (col.type === "number" || col.type === "price") {
      setValue(raw === null || raw === undefined || raw === "" ? "" : fmtPrice(raw));
    } else {
      setValue(raw ?? "");
    }
  }, [task, col.key, col.type]);

  if (col.key === "name") {
    const link = hrefFor(task.url);
    const commitName = () => {
      if (value === (task.name ?? "")) return;
      if (!String(value).trim()) {
        setValue(task.name ?? "");
        return;
      }
      onSave(task.id, "name", value);
    };
    return (
      <div className="name-cell">
        {canEdit ? (
          <input
            className="cell-input name-input"
            type="text"
            value={value ?? ""}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          />
        ) : (
          <span className="ro name-text" title={task.name}>{task.name || "—"}</span>
        )}
        <span className="name-cell-actions">
          {link && (
            <a
              className="task-link-icon"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              title={task.url}
              onClick={(e) => e.stopPropagation()}
            >
              <LinkIcon />
            </a>
          )}
          <button
            type="button"
            className="ghost icon name-expand"
            title="View full details"
            onClick={onOpenDetail}
          >
            ⤢
          </button>
        </span>
      </div>
    );
  }

  if (col.type === "assigned") {
    const assignments = task.assignments || [];
    return (
      <div
        className="assigned-chips"
        onClick={canEdit ? onOpenAssignments : undefined}
        title={canEdit ? "Click to edit assignments" : undefined}
        style={!canEdit ? { cursor: "default" } : undefined}
      >
        {assignments.length === 0 ? (
          <span className="assigned-empty">{canEdit ? "+ assign" : "—"}</span>
        ) : (
          assignments.map((a) => (
            <span key={a.user_id} className="assigned-chip">
              {a.name}
              {a.fixed_amount != null && (
                <span className="chip-amount">{currencySymbol}{fmtPrice(a.fixed_amount)}</span>
              )}
            </span>
          ))
        )}
      </div>
    );
  }

  if (col.type === "seq") {
    const n = task.project_seq;
    return <span className="ro num seq-num">{n === null || n === undefined ? "—" : n}</span>;
  }

  if (col.type === "my_pay") {
    const v = task.my_pay;
    if (v === null || v === undefined) return <span className="ro empty">—</span>;
    return <span className="ro num">{currencySymbol}{fmtPrice(v)}</span>;
  }

  if (col.type === "bool") {
    return (
      <input
        type="checkbox"
        className="cbx"
        checked={!!task[col.key]}
        disabled={!canEdit}
        onChange={(e) => onSave(task.id, col.key, e.target.checked)}
      />
    );
  }

  if (!canEdit) {
    const v = task[col.key];
    if (v === null || v === undefined || v === "")
      return <span className="ro empty">—</span>;
    if (col.type === "price")
      return (
        <span className="ro num">
          {Number(v) === 0 ? "Free" : currencySymbol + fmtPrice(v)}
        </span>
      );
    if (col.type === "number")
      return <span className="ro num">{fmtPrice(v)}</span>;
    return <span className="ro">{String(v)}</span>;
  }

  const isNumber = col.type === "number" || col.type === "price";
  const isText = col.type === "textarea";

  const commit = () => {
    let v = value;
    if (isNumber) v = num(v);
    if (v === task[col.key]) return;
    onSave(task.id, col.key, v);
  };

  if (isText) {
    return (
      <textarea
        className="cell-textarea"
        value={value || ""}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        rows={1}
      />
    );
  }

  if (col.type === "price") {
    return (
      <div className="price-cell">
        <span className="prefix">{currencySymbol}</span>
        <input
          className="cell-input num with-currency"
          type="number"
          step="5"
          value={value ?? ""}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        />
      </div>
    );
  }

  return (
    <input
      className={`cell-input ${isNumber ? "num" : ""}`}
      type={isNumber ? "number" : "text"}
      step={isNumber ? "5" : undefined}
      value={value ?? ""}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
    />
  );
}
